import { List, ListItem, ListItemIcon, ListItemText, Typography, Box } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import makeStyles from '@mui/styles/makeStyles';
import { DeloitteInfo, DeloitteSection, DeloitteCard, DeloitteServiceCard, DeloitteText, DeloitteDialog } from 'TaxAnalyticsUI/Deloitte';
import { ErrorOutline, InsertDriveFileOutlined as File } from '@mui/icons-material';
import { useHistory, useParams } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { Paragraph, DataType, IOffer } from 'TaxAnalyticsComponents/mock/types';
import { useRecoilValue } from 'recoil';
import { serviceOffersState } from 'TaxAnalyticsState/portalState';
import { ServiceOfferLevelDTO } from 'TaxAnalyticsHelpers/TaxAnalyticsClient';
import { FlattenedOffers } from 'TaxAnalyticsComponents/mock/Offers';
import { EInvoicingCalendar } from 'TaxAnalyticsComponents/mock/EInvoicingCalendar';
import { tag } from 'TaxAnalyticsHelpers/ATInternet';

const useStyles = makeStyles((theme) => ({
    listItemText: {
        fontWeight: 300,
        fontSize: '18px',
        color: theme.palette.text.secondary,
        lineHeight: '28px'
    }
}));

const generateText = (elements: Paragraph[], parentType?: DataType) => {
    return elements.map((row, index) =>
        <Fragment key={index}>
            {
                row.type === DataType.Text &&
                row.data?.map((data: any, index: number) =>
                    <DeloitteText key={index}>
                        {data}
                    </DeloitteText>
                )
            }
            {
                row.type === DataType.List &&
                <DeloitteText component="ul">
                    {
                        !row.children && row.data?.map((data: any, index: number) => <li key={index}>{data}</li>)
                    }
                    {
                        row.children &&
                        row.children.map((child, i) =>
                            <li>
                                {child.data?.map((data: any, index: number) => data)}
                                {
                                    child.children && generateText(child.children, row.type)
                                }
                            </li>
                        )
                    }
                </DeloitteText>
            }
            {
                row.type === DataType.Caldendar &&
                <EInvoicingCalendar />
            }
        </Fragment>
    )
}

type OfferParams = {
    name: string;
}

export function Offer() {
    const classes = useStyles();
    const history = useHistory();
    const [modalOpen, setModalOpen] = useState(false);
    const { name } = useParams<OfferParams>();
    const serviceOffers = useRecoilValue(serviceOffersState);
    const selectLevel = (level: ServiceOfferLevelDTO, offerInfo: IOffer) => {
        history.push(offerInfo.href!, { level });
    }
    const currentOffer = serviceOffers.find(offer => offer?.code?.toLowerCase() === name?.toLowerCase());
    const offerInfo = FlattenedOffers.find(offer => offer.id.toLowerCase() === name?.toLowerCase());

    useEffect(() => {
        tag.sendPage({
            name: `Détail de l'offre: ${offerInfo?.title}`
        });
    }, [history.location.pathname]);

    if (offerInfo === undefined) {
        history.push('/');
        return <div />
    }

    return (
        <Grid container spacing={2} mt={4}>
            <Grid container component="section" id={offerInfo.id}>
                <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                    <Grid container xs={12} sm={8} p={0}>
                        <DeloitteInfo title={offerInfo.title} subtitle={offerInfo.subtitle} buttonLabel={offerInfo.buttonLabel} onClick={offerInfo.href ? () => history.push(offerInfo.href!) : undefined}>
                            {generateText(offerInfo.mainText)}
                        </DeloitteInfo>
                    </Grid>
                    {offerInfo.risks &&
                        <Grid xs={12} sm={4} p={0}>
                            <Box mt={5}>
                                <DeloitteCard title="Risques" buttonLabel={(offerInfo.risksDetail && offerInfo.risksDetail.length > 0) ? "En savoir plus" : undefined} variant="outlined" onClick={() => {
                                    setModalOpen(true);
                                    tag.sendPage({
                                        name: `Risques de l'offre: ${offerInfo?.title}`
                                    });
                                    tag.sendClick({
                                        name: `Risques de l'offre: ${offerInfo?.title}`
                                    })
                                }}>
                                    <List dense disablePadding>
                                        {offerInfo.risks.map(((risk, index) =>
                                            <ListItem key={index} disableGutters alignItems="flex-start">
                                                <ListItemIcon>
                                                    <ErrorOutline color="error" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        className={classes.listItemText}
                                                        color="textPrimary"
                                                    >
                                                        {risk}
                                                    </Typography>
                                                </ListItemText>
                                            </ListItem>
                                        ))}
                                    </List>
                                </DeloitteCard>
                            </Box>
                        </Grid>
                    }
                </Grid>
            </Grid>
            {offerInfo.why &&
                <DeloitteSection title={offerInfo.why.title} id="preps" variant="sides" justifyContent="space-between" >
                    <Grid xs={12}>
                        {
                            generateText(offerInfo.why.paragraph)
                        }
                    </Grid>
                    <Grid xs={12}>
                        {offerInfo.docs &&
                            <Box mt={2}>
                                <DeloitteCard title="Documents à communiquer" variant="outlined" color="primary" clear>
                                    <List dense disablePadding>
                                        {offerInfo.docs.map(((doc, index) =>
                                            <ListItem disableGutters alignItems="flex-start" key={index}>
                                                <ListItemIcon>
                                                    <File color="secondary" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        className={classes.listItemText}
                                                        color="textPrimary"
                                                    >
                                                        {doc}
                                                    </Typography>
                                                </ListItemText>
                                            </ListItem>
                                        ))}
                                    </List>
                                </DeloitteCard>
                            </Box>
                        }
                    </Grid>
                </DeloitteSection>
            }
            {currentOffer?.childServiceOffers && currentOffer.childServiceOffers.length > 1 &&
                <DeloitteSection title="Nos offres" id="offres">
                    <Grid container direction="row" alignItems="stretch" justifyContent="center" spacing={2}>
                        {currentOffer?.childServiceOffers.map((childOffer, index) => {
                            const service = offerInfo.subOffers?.find(o => o.id.toLowerCase() === childOffer.code?.toLowerCase());
                            if (service && service.tile)
                                return (
                                    <Grid xs={12} md={currentOffer!.childServiceOffers!.length < 4 ? 4 : 3} key={index}>
                                        <DeloitteServiceCard {...service.tile} onClick={() => history.push(service.tile!.href)} cta />
                                    </Grid>
                                )
                        }
                        )}
                    </Grid>
                </DeloitteSection>
            }
            {currentOffer?.serviceOfferLevels && currentOffer.serviceOfferLevels.length > 1 &&
                <DeloitteSection title="Nos offres" id="offres">
                    <Grid container direction="row" alignItems="stretch" justifyContent="center" spacing={2}>
                        {currentOffer?.serviceOfferLevels.map((level, index) => {
                            const offerLevelInfo = offerInfo.offerLevels?.find(l => l.code === level.code)
                            if (offerLevelInfo)
                                return (
                                    <Grid xs={12} md={currentOffer!.serviceOfferLevels!.length < 4 ? 4 : 3} key={index} order={offerLevelInfo.displayOrder}>
                                        <DeloitteCard text={offerLevelInfo.text} title={offerLevelInfo.title} buttonLabel="Analyser" cta onClick={() => { selectLevel(level, offerInfo); }}></DeloitteCard>
                                    </Grid>
                                )
                        }
                        )}
                    </Grid>
                </DeloitteSection>
            }
            <DeloitteDialog title="Les risques" open={modalOpen} closeCallback={() => setModalOpen(false)}>
                {
                    generateText(offerInfo.risksDetail ?? [])
                }
            </DeloitteDialog>
        </Grid>
    );
}